import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, ArrowUpRight, Sparkles, Loader2, AlertCircle } from 'lucide-react';
import { publicV2Api } from '../lib/api';

/**
 * /events — Public listing page.
 *
 * Sections:
 *   • Upcoming (sorted by nearest date first, hero priority)
 *   • Past archive (collapsed-style, dimmed)
 *
 * Design:
 *   • Mobile-first 4:5 image cards
 *   • Premium dark theme + gold/teal accents
 *   • Smooth hover/tap micro-animations
 */

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short',
    });
  } catch { return iso; }
};

const formatYear = (iso) => {
  try { return new Date(iso + 'T00:00:00').getFullYear(); } catch { return ''; }
};

const formatTime = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  if (isNaN(h)) return hhmm;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};

const fmtINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;


const EventCard = ({ event, dimmed = false, index = 0 }) => {
  const navigate = useNavigate();
  const fallback = 'https://images.unsplash.com/photo-1571266028243-e4733b0f0bb0?w=900&q=80';
  const img = event.mobile_image || event.hero_image || fallback;
  const priceLabel =
    event.min_price && event.min_price > 0
      ? (event.min_price === event.max_price ? fmtINR(event.min_price) : `From ${fmtINR(event.min_price)}`)
      : 'Free entry';

  return (
    <motion.button
      type="button"
      onClick={() => navigate(`/events/${event.slug}`)}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1], delay: 0.04 * Math.min(index, 6) }}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.985 }}
      className={`event-card ${dimmed ? 'event-card-dim' : ''}`}
      data-testid={`event-card-${event.slug}`}
    >
      <div className="event-card-image-wrap">
        <img
          src={img}
          alt={event.title}
          loading="lazy"
          className="event-card-image"
          onError={(e) => { e.currentTarget.src = fallback; }}
        />
        <div className="event-card-gradient" />
        {!dimmed && (
          <div className="event-card-date-badge" data-testid={`event-date-badge-${event.slug}`}>
            <span className="event-date-day">{new Date(event.event_date + 'T00:00:00').getDate()}</span>
            <span className="event-date-month">
              {new Date(event.event_date + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short' }).toUpperCase()}
            </span>
          </div>
        )}
        {dimmed && (
          <div className="event-past-pill">PAST · {formatYear(event.event_date)}</div>
        )}
      </div>

      <div className="event-card-body">
        <h3 className="event-card-title">{event.title}</h3>
        <div className="event-card-meta">
          <span className="event-card-meta-row">
            <Calendar size={12} className="text-[var(--accent-gold)]" />
            {formatDate(event.event_date)}
          </span>
          {event.event_time && (
            <span className="event-card-meta-row">
              <Clock size={12} className="text-[var(--accent-gold)]" />
              {formatTime(event.event_time)}
            </span>
          )}
        </div>
        {event.venue && (
          <p className="event-card-venue truncate">
            <MapPin size={12} className="inline mr-1 text-[var(--accent-gold)]" />
            {event.venue}
          </p>
        )}
        <div className="event-card-footer">
          <span className="event-card-price">{priceLabel}</span>
          <span className="event-card-cta">
            View <ArrowUpRight size={14} />
          </span>
        </div>
      </div>
    </motion.button>
  );
};


export default function EventsListPage() {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [u, p] = await Promise.all([
          publicV2Api.listEvents({ upcoming: true, past: false, limit: 50 }),
          publicV2Api.listEvents({ upcoming: false, past: true, limit: 12 }),
        ]);
        if (!alive) return;
        setUpcoming(u.data?.events || []);
        setPast(p.data?.events || []);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || 'Failed to load events');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  const featured = upcoming[0];
  const restUpcoming = upcoming.slice(1);

  return (
    <div className="events-page" data-testid="events-page">
      <Helmet>
        <title>Events · Scratch Unleashed</title>
        <meta name="description" content="Upcoming nights, parties, and signature events at Scratch Unleashed. Book your passes in seconds." />
        <link rel="canonical" href="https://scratchjsr.com/events" />
        <meta property="og:url" content="https://scratchjsr.com/events" />
        <meta property="og:title" content="Events · Scratch Unleashed" />
        <meta property="og:description" content="Upcoming nights, parties, and signature events at Scratch Unleashed." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* ── Page Header ─────────────────────────────────────── */}
      <header className="events-header">
        <Link to="/" className="events-back-link" data-testid="events-back-home">
          ← Back to Home
        </Link>
        <div className="events-header-inner">
          <span className="events-eyebrow">
            <Sparkles size={12} className="text-[var(--accent-gold)]" />
            Tickets · Tables · Experiences
          </span>
          <h1 className="events-title">Upcoming<br className="sm:hidden" /> at Scratch</h1>
          <p className="events-subtitle">
            Curated nights, signature shows, and after-dark experiences. Book your passes ahead — entry is limited.
          </p>
        </div>
      </header>

      <main className="events-main">
        {loading ? (
          <div className="events-loading" data-testid="events-loading">
            <Loader2 size={26} className="animate-spin text-[var(--accent-gold)]" />
            <p>Loading events…</p>
          </div>
        ) : error ? (
          <div className="events-error" data-testid="events-error">
            <AlertCircle size={28} className="text-red-400 mb-2" />
            <p className="text-zinc-300">Couldn't load events right now.</p>
            <button className="btn-gold mt-3" onClick={() => window.location.reload()}>Retry</button>
          </div>
        ) : (
          <>
            {/* ── Featured Hero ───────────────────────────────── */}
            {featured && (
              <motion.section
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="events-featured"
                data-testid="events-featured"
              >
                <Link to={`/events/${featured.slug}`} className="events-featured-link">
                  <div className="events-featured-imgwrap">
                    <img
                      src={featured.hero_image || featured.mobile_image || 'https://images.unsplash.com/photo-1571266028243-e4733b0f0bb0?w=1400&q=85'}
                      alt={featured.title}
                      className="events-featured-img"
                      loading="eager"
                    />
                    <div className="events-featured-overlay" />
                  </div>
                  <div className="events-featured-content">
                    <span className="events-featured-tag">NEXT UP</span>
                    <h2 className="events-featured-title">{featured.title}</h2>
                    <div className="events-featured-meta">
                      <span><Calendar size={13} /> {formatDate(featured.event_date)}</span>
                      {featured.event_time && <span><Clock size={13} /> {formatTime(featured.event_time)}</span>}
                      {featured.venue && <span><MapPin size={13} /> {featured.venue}</span>}
                    </div>
                    <div className="events-featured-cta">
                      <span>
                        {featured.min_price > 0
                          ? `From ${fmtINR(featured.min_price)}`
                          : 'Free entry'}
                      </span>
                      <span className="events-featured-arrow">
                        Book Passes <ArrowUpRight size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.section>
            )}

            {/* ── Upcoming Grid ───────────────────────────────── */}
            {restUpcoming.length > 0 && (
              <section className="events-section" data-testid="events-upcoming">
                <header className="events-section-header">
                  <h2>More Upcoming</h2>
                  <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                    {restUpcoming.length} event{restUpcoming.length === 1 ? '' : 's'}
                  </span>
                </header>
                <div className="events-grid">
                  {restUpcoming.map((ev, i) => <EventCard key={ev._id} event={ev} index={i} />)}
                </div>
              </section>
            )}

            {/* ── Empty State (no upcoming) ───────────────────── */}
            {upcoming.length === 0 && (
              <div className="events-empty" data-testid="events-empty">
                <Sparkles size={36} className="text-[var(--accent-gold)] mb-3" />
                <h3 className="text-lg text-white font-semibold mb-2">No upcoming events</h3>
                <p className="text-sm text-zinc-400 max-w-sm text-center">
                  We're brewing something special. Check back soon, or follow us on social for first dibs.
                </p>
              </div>
            )}

            {/* ── Past Archive ────────────────────────────────── */}
            {past.length > 0 && (
              <section className="events-section events-past" data-testid="events-past">
                <header className="events-section-header">
                  <h2 className="text-zinc-300">Past Events</h2>
                  <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                    Memories
                  </span>
                </header>
                <div className="events-grid">
                  {past.map((ev, i) => <EventCard key={ev._id} event={ev} dimmed index={i} />)}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
